import type { AccountState } from "@aspen/types";
import { useAuth } from "@clerk/vue";
import { ref, computed, watch } from "vue";
import { useApi } from "@/lib/api";

const STORAGE_KEY = "pando:account-state";

// Restore cached state from sessionStorage on module load
// This allows route guards to have immediate access to role info on page refresh
function getInitialState(): AccountState | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached) as AccountState;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveState(state: AccountState | null) {
  if (typeof window === "undefined") return;
  try {
    if (state) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
  }
}

const accountStateRef = ref<AccountState | null>(getInitialState());
const accountLoadingRef = ref(false);
const accountErrorRef = ref<string | null>(null);
// Track whether we've completed at least one successful fetch this session
const accountInitializedRef = ref(accountStateRef.value !== null);

// Persist state changes to sessionStorage
watch(accountStateRef, (newState) => {
  saveState(newState);
  if (newState !== null) {
    accountInitializedRef.value = true;
  }
});

export function useAccount() {
  const { apiFetch } = useApi();
  const auth = useAuth();

  async function refresh() {
    const userId = (auth.userId as { value: string | null }).value;
    if (!userId) {
      accountStateRef.value = null;
      accountErrorRef.value = null;
      accountInitializedRef.value = false;
      return;
    }
    accountLoadingRef.value = true;
    accountErrorRef.value = null;
    try {
      const response = await apiFetch("/api/account", { method: "GET" });
      const payload = (await response.json()) as AccountState;
      accountStateRef.value = payload;
    } catch (error) {
      accountErrorRef.value =
        error instanceof Error && error.message.includes("404")
          ? "We could not find your employee profile yet. Try signing out and back in."
          : "We could not load your workspace status. Please try again.";
    } finally {
      accountLoadingRef.value = false;
    }
  }

  function clearCache() {
    accountStateRef.value = null;
    accountInitializedRef.value = false;
    saveState(null);
  }

  // Consumers control when to refresh to avoid duplicate network calls.

  return {
    state: accountStateRef,
    loading: accountLoadingRef,
    error: accountErrorRef,
    // Whether we have valid cached or fetched state (helps guards decide)
    initialized: accountInitializedRef,
    needsCompany: computed(
      () => accountStateRef.value?.state === "needs_company",
    ),
    needsOnboarding: computed(
      () =>
        accountStateRef.value?.state === "ready" &&
        accountStateRef.value.onboardingCompletedAt === null,
    ),
    refresh,
    clearCache,
  };
}
